import { Text, View, Image, StyleSheet } from "@react-pdf/renderer";
import {
  formatDateYMD,
  formatWithMonthAsText,
} from "../../../services/mappings";
import logoAmsa from "../logo.jpg";

const styles = StyleSheet.create({
  wrapper: {
    fontSize: 8,
  },
  row: {
    flexDirection: "row",
  },

  left: {
    width: "25%",
    border: "1.5pt solid #222",
    borderRight: "none",
  },
  center: {
    width: "50%",
    border: "1.5pt solid #222",
  },
  right: {
    width: "25%",
    border: "1.5pt solid #222",
    borderLeft: "none",
  },

  logoCell: {
    height: 50,
  },
  logo: {
    width: 140,
    height: 100,
    objectFit: "fill",
  },

  cell: {
    minHeight: 18,
    borderBottom: "1pt solid #222",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  cellLast: {
    minHeight: 18,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 4,
    paddingVertical: 2,
  },

  label: {
    fontSize: 7.5,
  },
  value: {
    marginLeft: "auto",
    fontSize: 8,
    fontWeight: "bold",
    color: "#0369a1",
    textAlign: "right",
  },

  titleCell: {
    height: 54,
    borderBottom: "1pt solid #222",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 8,
  },
  title: {
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center",
  },

  serviceCell: {
    height: 18,
    justifyContent: "center",
    paddingHorizontal: 6,
  },
  serviceLabel: {
    fontSize: 8,
  },
  serviceValue: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#0369a1",
  },

  rightTop: {
    height: 51,
    paddingHorizontal: 4,
    paddingVertical: 2,
    justifyContent: "center",
  },
  rightLine: {
    minHeight: 18,
    flexDirection: "row",
    alignItems: "center",
  },
});

function DocumentHeader({ headers }) {
  const data = headers?.data ?? headers ?? {};

  const toDM = (value) => {
    const ymd = formatDateYMD(value);
    if (!ymd) return "";
    const [, m, d] = ymd.split("-");
    return `${d}/${m}`;
  };

  const toDMY = (value) => {
    const ymd = formatDateYMD(value);
    if (!ymd) return "";
    const [y, m, d] = ymd.split("-");
    return `${d}-${m}-${y}`;
  };

  const pedidoAbierto =
    data?.pedidoAbierto ??
    data?.pedido_abierto ??
    data?.pedidO_ABIERTO ??
    data?.nroPedidoAbierto ??
    "";

  const pedidoEspejo =
    data?.pedidoEspejo ??
    data?.pedido_espejo ??
    data?.pedidO_ESPEJO ??
    data?.contracT_CODE ??
    "";

  const fechaDesde =
    data?.fechA_DESDE ?? data?.fecha_desde ?? data?.fechaDesde ?? "";
  const fechaHasta =
    data?.fechA_HASTA ?? data?.fecha_hasta ?? data?.fechaHasta ?? "";

  const periodoPagoFormateado =
    toDM(fechaDesde) && toDM(fechaHasta)
      ? `${toDM(fechaDesde)} al ${toDM(fechaHasta)}`
      : "";

  const periodoPago =
    periodoPagoFormateado ||
    data?.periodoPago ||
    data?.periodo_pago ||
    data?.periodO_PAGO ||
    data?.meS_EP ||
    "";

  const moneda =
    data?.moneda === "CLP"
      ? "Pesos"
      : data?.moneda === "USD"
        ? "Dólares"
        : (data?.moneda ?? "");

  const estadoPago = data?.estadoPago ?? data?.nrO_EP ?? "";

  const fechaPresentacion = toDMY(
    data?.fechaPresentacion ?? data?.fecha_preparacion,
  );
  const fechaConversion = toDMY(
    data?.fechaConversion ?? data?.fechA_TIPO_CAMBIO,
  );

  const titulo = `ESTADO DE PAGO N°${data?.nrO_EP ?? "XX"} ${
    data?.contracT_EECC ?? "Nombre Empresa"
  } ${formatWithMonthAsText(data?.meS_EP ?? "")}`;

  const nombreServicio =
    data?.nombreServicio ??
    data?.nombre_servicio ??
    data?.contracT_NAME ??
    "Nombre Servicio";

  return (
    <View style={styles.wrapper}>
      <View style={styles.row}>
        <View style={styles.logoCell}>
          <Image src={logoAmsa} style={styles.logo} />
        </View>
      </View>
      <View style={styles.row}>
        <View style={styles.left}>
          <View style={styles.cell}>
            <Text style={styles.label}>Pedido Abierto N° :</Text>
            <Text style={styles.value}>{pedidoAbierto}</Text>
          </View>

          <View style={styles.cell}>
            <Text style={styles.label}>Pedido Espejo N° :</Text>
            <Text style={styles.value}>{pedidoEspejo}</Text>
          </View>

          <View style={styles.cell}>
            <Text style={styles.label}>Contratista :</Text>
            <Text
              hyphenationCallback={(word) => [word]}
              style={[styles.value, { maxWidth: "77%" }]}
            >
              {data?.contracT_EECC ?? ""}
            </Text>
          </View>

          <View style={styles.cellLast}>
            <Text style={styles.label}>Estado de Pago</Text>
            <Text style={styles.value}>{estadoPago}</Text>
          </View>
        </View>

        <View style={styles.center}>
          <View style={styles.titleCell}>
            <Text style={styles.title}>{titulo}</Text>
          </View>

          <View style={styles.serviceCell}>
            <Text style={styles.serviceLabel}>
              Nombre del Servicio :{" "}
              <Text style={styles.serviceValue}>{nombreServicio}</Text>
            </Text>
          </View>
        </View>

        <View style={styles.right}>
          <View style={styles.rightTop}>
            <View style={styles.rightLine}>
              <Text style={styles.label}>Fecha de presentación :</Text>
              <Text style={styles.value}>{fechaPresentacion}</Text>
            </View>
            <View style={styles.rightLine}>
              <Text style={styles.label}>Período de pago :</Text>
              <Text style={styles.value}>{periodoPago}</Text>
            </View>
            <View style={styles.rightLine}>
              <Text style={styles.label}>Moneda :</Text>
              <Text style={styles.value}>{moneda}</Text>
            </View>
          </View>

          <View style={styles.cellLast}>
            <Text style={styles.label}>Fecha Conversión :</Text>
            <Text style={styles.value}>{fechaConversion}</Text>
          </View>
        </View>
      </View>
    </View>
  );
}

export default DocumentHeader;
